/**
 * Portal público: enlaces coherentes con el embudo membresía (/registro → perfil → pendiente → acceso).
 */

import { Button } from "@/components/ui/button";
import { PortalCard, PortalShell } from "@/components/portal/PortalShell";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";
import {
  MEMBERSHIP_ACCESS_PORTAL_PATH,
  MEMBERSHIP_ROUTE,
} from "@/lib/membershipAccessFlow";
import { MEMBERSHIP_PORTAL_STEPS } from "@/lib/membershipPortalSteps";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  LogIn,
  Sparkles,
  UserPlus,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_BY_PATH: Record<string, LucideIcon> = {
  [MEMBERSHIP_ROUTE.REGISTRO]: CreditCard,
  [MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO]: UserPlus,
  [MEMBERSHIP_ROUTE.PENDING_APPROVAL]: Clock,
  [MEMBERSHIP_ROUTE.MI_ACCESO]: CheckCircle2,
  [MEMBERSHIP_ROUTE.ENTRAR]: LogIn,
};

const SEO = {
  title: `Pasos de acceso a la membresía — ${BRAND_PERSON_NAME}`,
  description: `Después del pago: crear cuenta, completar perfil, esperar la aprobación del equipo y obtener el link de ingreso.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ACCESS_PORTAL_PATH}`,
  robots: "noindex, nofollow" as const,
};

export default function MembershipAccessPortal() {
  useSEO(SEO);

  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : (BRAND_SITE_URL || "").replace(/\/$/, "");

  return (
    <PortalShell centered={false} className="bg-zinc-950 text-zinc-100">
      <div className="mx-auto max-w-2xl space-y-8">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium tracking-wide text-amber-200 ring-1 ring-amber-500/30">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Membresía
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-50">
            Pasos para acceder a tu membresía
          </h1>
          <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
            Mismo orden que después del pago en {BRAND_PERSON_NAME || "este sitio"}: elegí la
            opción que coincide con tu situación.
          </p>
        </div>

        <ol className="space-y-4 list-none p-0 m-0">
          {MEMBERSHIP_PORTAL_STEPS.map((step, index) => {
            const Icon = ICON_BY_PATH[step.path] ?? UserPlus;
            const highlight = step.highlight === true;
            return (
              <li key={step.path}>
                <PortalCard
                  highlight={highlight}
                  className="p-4 sm:p-5"
                >
                  <div className="flex items-start gap-3 pb-3">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums ${
                        highlight
                          ? "bg-amber-500/25 text-amber-100 ring-1 ring-amber-500/35"
                          : "bg-white/10 text-zinc-300"
                      }`}
                      aria-hidden
                    >
                      {index + 1}
                    </div>
                    <div
                      className={`rounded-lg p-2 shrink-0 ${
                        highlight ? "bg-amber-500/20 text-amber-200" : "bg-white/5 text-zinc-300"
                      }`}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <h2 className="text-base font-semibold text-zinc-100 leading-snug">
                        {step.title}
                      </h2>
                      <p className="text-zinc-400 text-sm leading-relaxed">{step.body}</p>
                    </div>
                  </div>

                  <div className="pt-1">
                    <Button
                      asChild
                      className={
                        highlight
                          ? "portal-press w-full sm:w-auto bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold"
                          : "portal-press w-full sm:w-auto bg-white text-zinc-950 hover:bg-white/90 font-semibold"
                      }
                    >
                      <Link
                        to={step.path}
                        className="gap-2 inline-flex items-center justify-center"
                      >
                        {step.cta}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </Button>
                    <p className="mt-2 text-[11px] font-mono text-zinc-500 tabular-nums break-all">
                      {origin}
                      {step.path}
                    </p>
                  </div>
                </PortalCard>
              </li>
            );
          })}
        </ol>

        {BRAND_SUPPORT_EMAIL ? (
          <p className="text-center text-xs text-zinc-500">
            ¿Seguís con dudas? Escribinos a{" "}
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="portal-press text-amber-300/90 underline underline-offset-2 hover:text-amber-200"
            >
              {BRAND_SUPPORT_EMAIL}
            </a>{" "}
            con el correo con el que pagaste.
          </p>
        ) : null}

        <p className="text-center">
          <Link
            to="/"
            className="portal-press text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
          >
            Volver al inicio
          </Link>
        </p>
      </div>
    </PortalShell>
  );
}
